import { useState } from "react";
import { Alert, Button, ConfirmDialog, ToggleInput } from "@a2z/ui";
import type { AdminAreaView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "@admin/app/locale";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useUpdateAreaMutation } from "../services/useUpdateAreaMutation";
import { useDeleteAreaMutation } from "../services/useDeleteAreaMutation";
import { AreaFormDialog } from "./AreaFormDialog";

type Props = {
  area: AdminAreaView;
};

// Single area row: localized name (Arabic name is the source of truth, the
// English name sits beneath), an active toggle, and edit/delete actions.
export function AreaRow({ area }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const updateArea = useUpdateAreaMutation();
  const deleteArea = useDeleteAreaMutation();
  const [error, setError] = useState<string | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const isPending = updateArea.isPending || deleteArea.isPending;
  const primary = locale === "ar" ? area.nameAr : area.nameEn ?? area.nameAr;
  const secondary = locale === "ar" ? area.nameEn : area.nameAr;

  async function toggleActive(next: boolean): Promise<void> {
    setError(null);
    try {
      await updateArea.mutateAsync({ id: area.id, isActive: next });
    } catch (err) {
      setError(messageFor(err, t));
    }
  }

  async function performDelete(): Promise<void> {
    setError(null);
    try {
      await deleteArea.mutateAsync({ id: area.id });
    } catch (err) {
      setError(messageFor(err, t));
      setIsConfirmOpen(false);
    }
  }

  return (
    <li className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 sm:px-5 sm:py-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">
            {primary}
          </h3>
          {secondary ? (
            <p className="mt-0.5 text-xs text-[color:var(--text-muted)]">
              {secondary}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <label className="flex items-center gap-2 text-xs font-medium text-[color:var(--text-secondary)]">
            <ToggleInput
              name={`area-active-${area.id}`}
              ariaLabel={t(keys.areas.activeLabel)}
              checked={area.isActive}
              disabled={isPending}
              onChange={(event) => toggleActive(event.target.checked)}
            />
            {t(keys.common.active)}
          </label>
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsEditOpen(true)}
            disabled={isPending}
            className="h-8 rounded-md px-3 text-xs font-semibold max-lg:h-10"
          >
            {t(keys.common.edit)}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsConfirmOpen(true)}
            disabled={isPending}
            className="h-8 rounded-md px-3 text-xs font-semibold text-[color:var(--danger-600)] max-lg:h-10"
          >
            {t(keys.common.delete)}
          </Button>
        </div>
      </div>
      {error ? (
        <div className="mt-3">
          <Alert message={error} />
        </div>
      ) : null}

      {isEditOpen ? (
        <AreaFormDialog
          isOpen
          onClose={() => setIsEditOpen(false)}
          area={area}
        />
      ) : null}
      <ConfirmDialog
        isOpen={isConfirmOpen}
        title={t(keys.areas.deleteAction)}
        body={t(keys.areas.confirmDelete)}
        confirmLabel={t(keys.common.delete)}
        cancelLabel={t(keys.common.cancel)}
        intent="danger"
        isSubmitting={deleteArea.isPending}
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={performDelete}
      />
    </li>
  );
}

function messageFor(
  err: unknown,
  t: ReturnType<typeof useTranslations>,
): string {
  if (err instanceof ApiJsonClientError) {
    return err.message || t(keys.errors.generic);
  }
  return t(keys.errors.generic);
}
